import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, ShieldCheck, FileText, ScrollText, Users,
  ClipboardCheck, Building2, Landmark, AlertTriangle,
} from 'lucide-react';
import HussarTopNav from '../components/HussarTopNav';

/**
 * DlaInstytucji — v4.0 Phase 4 (fleshed).
 *
 * Full institutional / authorised-team audience surface for HussarAi's
 * immigration record integrity infrastructure. Renders locked hero copy,
 * five deep sections (use-cases, human authority, review signals, audit,
 * pilot formats), and the shared safety doctrine.
 *
 * No backend calls. Public route.
 */
const PAGE_BG = 'var(--hussar-bg-page, #0B1220)';
const TEXT_PRIMARY = 'var(--hussar-text-primary, #E5E7EB)';
const CYAN = '#7DD3FC';
const CYAN_STRONG = '#38BDF8';
const RED_SOFT = 'rgba(248,113,113,0.30)';

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith('meta') ? 'meta' : 'link');
    const name = selector.match(/name="([^"]+)"/)?.[1];
    const prop = selector.match(/property="([^"]+)"/)?.[1];
    const rel = selector.match(/rel="([^"]+)"/)?.[1];
    if (name) el.setAttribute('name', name);
    if (prop) el.setAttribute('property', prop);
    if (rel) el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const cardStyle = {
  background: 'rgba(15,23,42,0.55)',
  border: '1px solid rgba(148,163,184,0.15)',
  borderRadius: 14,
  padding: '26px 26px 22px',
  marginBottom: 22,
};

const iconChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 34,
  height: 34,
  borderRadius: 8,
  background: 'rgba(56,189,248,0.10)',
  border: '1px solid rgba(56,189,248,0.30)',
  color: CYAN_STRONG,
  marginBottom: 12,
};

const sectionTitleStyle = {
  fontSize: 20,
  fontWeight: 700,
  color: '#F1F5F9',
  margin: '0 0 10px',
  letterSpacing: -0.2,
};

const sectionBodyStyle = {
  fontSize: 15,
  lineHeight: 1.65,
  color: 'rgba(226,232,240,0.86)',
  margin: '0 0 12px',
};

const bulletListStyle = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'grid',
  gap: 8,
};

const bulletItemStyle = {
  fontSize: 14,
  lineHeight: 1.55,
  color: 'rgba(226,232,240,0.88)',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
};

const bulletDotStyle = {
  flex: '0 0 auto',
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: CYAN,
  marginTop: 8,
};

const DlaInstytucji = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('dlaInstytucji.metaTitle');
    setMeta('meta[name="description"]', 'content', t('dlaInstytucji.metaDescription'));
    setMeta('meta[property="og:title"]', 'content', t('dlaInstytucji.metaTitle'));
    setMeta('meta[property="og:description"]', 'content', t('dlaInstytucji.metaDescription'));
    setMeta('link[rel="canonical"]', 'href', 'https://hussarai.org/dla-instytucji');
  }, [t]);

  const useCaseItems = t('dlaInstytucji.useCases.items', { returnObjects: true }) || [];
  const reviewSignalItems = t('dlaInstytucji.reviewSignals.items', { returnObjects: true }) || [];
  const auditItems = t('dlaInstytucji.audit.items', { returnObjects: true }) || [];
  const pilotItems = t('dlaInstytucji.pilotFormats.items', { returnObjects: true }) || [];

  return (
    <main
      data-testid="dla-instytucji-page"
      style={{ minHeight: '100vh', background: PAGE_BG, color: TEXT_PRIMARY, padding: '56px 20px 96px' }}
    >
      <HussarTopNav active="forInstitutions" />
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <Link
          to="/"
          data-testid="dla-instytucji-back-link"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'rgba(125,211,252,0.85)', textDecoration: 'none', marginBottom: 28 }}
        >
          <ArrowLeft className="w-4 h-4" /> HussarAi
        </Link>

        {/* Hero */}
        <p data-testid="dla-instytucji-eyebrow" style={{ fontSize: 11, letterSpacing: 1.6, fontWeight: 700, color: CYAN, textTransform: 'uppercase', marginBottom: 14 }}>
          {t('dlaInstytucji.eyebrow')}
        </p>
        <h1 data-testid="dla-instytucji-h1" style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, lineHeight: 1.2, marginBottom: 18, color: '#F1F5F9' }}>
          {t('dlaInstytucji.heroTitle')}
        </h1>
        <p data-testid="dla-instytucji-subtitle" style={{ fontSize: 17, lineHeight: 1.65, color: 'rgba(226,232,240,0.9)', marginBottom: 20 }}>
          {t('dlaInstytucji.heroSubtitle')}
        </p>
        <p data-testid="dla-instytucji-positioning" style={{ fontSize: 15, lineHeight: 1.65, color: 'rgba(226,232,240,0.75)', marginBottom: 24 }}>
          {t('dlaInstytucji.positioning')}
        </p>

        {/* Trust card */}
        <div data-testid="dla-instytucji-trust-card" style={{ padding: '18px 20px', marginBottom: 20, background: 'rgba(248,113,113,0.06)', border: `1px solid ${RED_SOFT}`, borderLeft: '3px solid #F87171', borderRadius: 12 }}>
          <p style={{ fontSize: 12, letterSpacing: 1.2, fontWeight: 700, color: '#F87171', margin: 0, textTransform: 'uppercase' }}>Granica systemu</p>
          <p data-testid="dla-instytucji-trust" style={{ fontSize: 14, lineHeight: 1.6, marginTop: 8, color: 'rgba(226,232,240,0.92)' }}>
            {t('dlaInstytucji.trust')}
          </p>
        </div>

        {/* Positioning helper for tests + humans */}
        <p data-testid="dla-instytucji-stub-notice" style={{ fontSize: 13, lineHeight: 1.55, color: 'rgba(226,232,240,0.72)', marginBottom: 28 }}>
          {t('dlaInstytucji.stubNotice')}
        </p>

        {/* Top CTA row */}
        <div data-testid="dla-instytucji-cta-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 44 }}>
          <Link to="/review-akt-imigracyjnych" data-testid="dla-instytucji-cta-review-demo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 8, fontWeight: 700, background: CYAN, color: '#0B1220', textDecoration: 'none', fontSize: 15 }}>
            <FileText className="w-4 h-4" aria-hidden="true" />
            {t('dlaInstytucji.ctaCaseHub')}
          </Link>
          <Link to="/moje-akta-imigracyjne" data-testid="dla-instytucji-cta-moje-akta" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 8, fontWeight: 600, background: 'rgba(15,23,42,0.55)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(226,232,240,0.92)', textDecoration: 'none', fontSize: 14 }}>
            <Users className="w-4 h-4" aria-hidden="true" />
            {t('dlaInstytucji.ctaMojeAkta')}
          </Link>
        </div>

        {/* Section 1 — Use cases */}
        <section data-testid="dla-instytucji-usecases" style={cardStyle}>
          <span style={iconChipStyle}><ClipboardCheck className="w-4 h-4" aria-hidden="true" /></span>
          <h2 data-testid="dla-instytucji-usecases-title" style={sectionTitleStyle}>{t('dlaInstytucji.useCases.title')}</h2>
          <p style={sectionBodyStyle}>{t('dlaInstytucji.useCases.intro')}</p>
          <ul style={bulletListStyle} data-testid="dla-instytucji-usecases-list">
            {useCaseItems.map((item) => (
              <li key={item} style={bulletItemStyle}>
                <span style={bulletDotStyle} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Section 2 — Human authority */}
        <section data-testid="dla-instytucji-human-authority" style={cardStyle}>
          <span style={iconChipStyle}><ShieldCheck className="w-4 h-4" aria-hidden="true" /></span>
          <h2 data-testid="dla-instytucji-human-authority-title" style={sectionTitleStyle}>{t('dlaInstytucji.humanAuthority.title')}</h2>
          <p style={sectionBodyStyle}>{t('dlaInstytucji.humanAuthority.body')}</p>
        </section>

        {/* Section 3 — Review signals */}
        <section data-testid="dla-instytucji-review-signals" style={cardStyle}>
          <span style={iconChipStyle}><AlertTriangle className="w-4 h-4" aria-hidden="true" /></span>
          <h2 data-testid="dla-instytucji-review-signals-title" style={sectionTitleStyle}>{t('dlaInstytucji.reviewSignals.title')}</h2>
          <p style={sectionBodyStyle}>{t('dlaInstytucji.reviewSignals.intro')}</p>
          <ul style={bulletListStyle} data-testid="dla-instytucji-review-signals-list">
            {reviewSignalItems.map((item) => (
              <li key={item} style={bulletItemStyle}>
                <span style={bulletDotStyle} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p data-testid="dla-instytucji-review-signals-footer" style={{ ...sectionBodyStyle, marginTop: 14, fontStyle: 'italic', color: 'rgba(226,232,240,0.72)' }}>
            {t('dlaInstytucji.reviewSignals.footer')}
          </p>
        </section>

        {/* Section 4 — Audit trail */}
        <section data-testid="dla-instytucji-audit" style={cardStyle}>
          <span style={iconChipStyle}><ScrollText className="w-4 h-4" aria-hidden="true" /></span>
          <h2 data-testid="dla-instytucji-audit-title" style={sectionTitleStyle}>{t('dlaInstytucji.audit.title')}</h2>
          <p style={sectionBodyStyle}>{t('dlaInstytucji.audit.intro')}</p>
          <ul style={bulletListStyle} data-testid="dla-instytucji-audit-list">
            {auditItems.map((item) => (
              <li key={item} style={bulletItemStyle}>
                <span style={bulletDotStyle} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Section 5 — Pilot formats */}
        <section data-testid="dla-instytucji-pilot-formats" style={cardStyle}>
          <span style={iconChipStyle}><Landmark className="w-4 h-4" aria-hidden="true" /></span>
          <h2 data-testid="dla-instytucji-pilot-formats-title" style={sectionTitleStyle}>{t('dlaInstytucji.pilotFormats.title')}</h2>
          <p style={sectionBodyStyle}>{t('dlaInstytucji.pilotFormats.intro')}</p>
          <ul style={bulletListStyle} data-testid="dla-instytucji-pilot-formats-list">
            {pilotItems.map((item) => (
              <li key={item} style={bulletItemStyle}>
                <span style={bulletDotStyle} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/pilot-publiczny"
            data-testid="dla-instytucji-cta-pilot"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 18, padding: '10px 20px', borderRadius: 8, fontWeight: 700, background: 'rgba(56,189,248,0.15)', border: `1px solid ${CYAN}`, color: CYAN, textDecoration: 'none', fontSize: 14 }}
          >
            <Building2 className="w-4 h-4" aria-hidden="true" />
            {t('dlaInstytucji.pilotFormats.ctaLabel')}
          </Link>
        </section>

        {/* Shared safety strip */}
        <section
          data-testid="dla-instytucji-safety-strip"
          style={{ ...cardStyle, background: 'rgba(30,41,59,0.55)', borderLeft: `3px solid ${CYAN_STRONG}` }}
        >
          <p data-testid="dla-instytucji-safety-doctrine" style={{ ...sectionBodyStyle, color: 'rgba(226,232,240,0.92)', marginBottom: 10 }}>
            {t('dlaInstytucji.safety.doctrine')}
          </p>
          <p data-testid="dla-instytucji-safety-integrity" style={{ ...sectionBodyStyle, color: 'rgba(226,232,240,0.88)', fontStyle: 'italic', marginBottom: 10 }}>
            {t('dlaInstytucji.safety.integrity')}
          </p>
          <p data-testid="dla-instytucji-safety-not-legal-advice" style={{ ...sectionBodyStyle, color: 'rgba(226,232,240,0.72)', marginBottom: 0, fontSize: 13 }}>
            {t('dlaInstytucji.safety.notLegalAdvice')}
          </p>
        </section>

        <p data-testid="dla-instytucji-doctrine-footer" style={{ marginTop: 36, fontSize: 12, lineHeight: 1.6, color: 'rgba(226,232,240,0.55)', fontStyle: 'italic' }}>
          AI przygotowuje. Człowiek decyduje. Każde działanie zostawia ślad audytowy.
        </p>
      </div>
    </main>
  );
};

export default DlaInstytucji;
